import React, { useState, useEffect } from 'react';
import { ShoppingCart, Download, Music, User, LogOut, CreditCard } from 'lucide-react';
import AudioPlayer from '../components/store/AudioPlayer';
import { getImageUrl } from '../utils/api';
import { toast } from 'sonner';
import './StorePage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorePage = () => {
  const [releases, setReleases] = useState([]);
  const [tracks, setTracks] = useState({});
  const [user, setUser] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [guestEmail, setGuestEmail] = useState(null);
  const [purchasedTrackIds, setPurchasedTrackIds] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('mp3'); // 'mp3' or 'wav'
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [purchasedTrack, setPurchasedTrack] = useState(null);
  const [purchasedFormat, setPurchasedFormat] = useState('mp3');

  // Pricing (CAD)
  const MP3_PRICE = 1.19;
  const WAV_PRICE = 2.49;

  // Check for PayPal return parameters in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get('paymentId');
    const payerId = params.get('PayerID');
    const paymentIdFromStore = params.get('payment_id');
    
    // Restore guest email from session storage
    const savedGuestEmail = sessionStorage.getItem('guestCheckoutEmail');
    if (savedGuestEmail) {
      setGuestEmail(savedGuestEmail);
    }
    
    // Restore purchased track IDs
    const savedPurchasedIds = sessionStorage.getItem('purchasedTrackIds');
    if (savedPurchasedIds) {
      setPurchasedTrackIds(JSON.parse(savedPurchasedIds));
    }
    
    if (paymentId && payerId) {
      // Execute PayPal payment
      executePayPalPayment(paymentId, payerId);
      // Clean up URL
      window.history.replaceState({}, '', '/store');
    } else if (paymentIdFromStore) {
      // Check payment status
      setCheckingPayment(true);
      pollPaymentStatus(paymentIdFromStore);
      window.history.replaceState({}, '', '/store');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user from localStorage
  useEffect(() => {
    const token = localStorage.getItem('storeToken');
    const userData = localStorage.getItem('storeUser');
    if (token && userData) {
      setUser(JSON.parse(userData));
      fetchPurchases(token);
    }
  }, []);

  // Fetch releases and their tracks
  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    try {
      const res = await fetch(`${API_URL}/api/releases`);
      const data = await res.json();
      setReleases(data);
      
      // Fetch tracks for each release
      for (const release of data) {
        const tracksRes = await fetch(`${API_URL}/api/store/releases/${release.id}/tracks`);
        const tracksData = await tracksRes.json();
        setTracks(prev => ({ ...prev, [release.id]: tracksData }));
      }
    } catch (err) {
      console.error('Failed to fetch releases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/store/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPurchases(data);
      }
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    }
  };

  const pollPaymentStatus = async (paymentId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/store/checkout/status/${paymentId}`);
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Payment successful! Your music is ready to download.');
        // Refresh purchases
        const token = localStorage.getItem('storeToken');
        if (token) fetchPurchases(token);
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(paymentId, attempts + 1), 2000);
    } catch (err) {
      console.error('Payment status check failed:', err);
      setTimeout(() => pollPaymentStatus(paymentId, attempts + 1), 2000);
    }
  };

  const executePayPalPayment = async (paymentId, payerId) => {
    setCheckingPayment(true);
    console.log('=== EXECUTING PAYPAL PAYMENT ===');
    console.log('paymentId:', paymentId);
    console.log('payerId:', payerId);
    
    try {
      const url = `${API_URL}/api/store/checkout/execute?paymentId=${paymentId}&PayerID=${payerId}`;
      console.log('Calling URL:', url);
      
      const res = await fetch(url);
      console.log('Response status:', res.status);
      
      const data = await res.json();
      console.log('Response data:', data);

      if (res.ok && data.payment_status === 'paid') {
        toast.success('Payment successful!');
        
        // Store the email for downloads
        if (data.user_email) {
          setGuestEmail(data.user_email);
          sessionStorage.setItem('guestCheckoutEmail', data.user_email);
        }
        
        // Get purchased track IDs from session storage
        const pendingTrackIds = sessionStorage.getItem('pendingPurchaseTrackIds');
        console.log('Pending track IDs:', pendingTrackIds);
        if (pendingTrackIds) {
          const trackIds = JSON.parse(pendingTrackIds);
          setPurchasedTrackIds(prev => [...new Set([...prev, ...trackIds])]);
          sessionStorage.setItem('purchasedTrackIds', JSON.stringify([...purchasedTrackIds, ...trackIds]));
          sessionStorage.removeItem('pendingPurchaseTrackIds');
        }
        
        // Show download modal with track info
        if (data.track) {
          setPurchasedTrack(data.track);
          setPurchasedFormat(data.format || 'mp3');
          setShowDownloadModal(true);
        }
        
        // Refresh purchases if logged in
        const token = localStorage.getItem('storeToken');
        if (token) fetchPurchases(token);
      } else {
        console.error('Payment failed:', data);
        toast.error(data.detail || 'Payment could not be completed.');
      }
    } catch (err) {
      console.error('Payment execution failed:', err);
      toast.error('Payment verification failed. Please contact support.');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const name = formData.get('name');

    const endpoint = authMode === 'login' ? '/api/store/login' : '/api/store/register';
    const body = authMode === 'login' 
      ? { email, password }
      : { email, password, name };

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      localStorage.setItem('storeToken', data.token);
      localStorage.setItem('storeUser', JSON.stringify(data.user));
      setUser(data.user);
      setShowAuthModal(false);
      fetchPurchases(data.token);
      toast.success(authMode === 'login' ? 'Welcome back!' : 'Account created!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('storeToken');
    localStorage.removeItem('storeUser');
    setUser(null);
    setPurchases([]);
    toast.info('Logged out');
  };

  const handleBuy = async (trackId, releaseId, itemName, price, format = 'mp3') => {
    const token = localStorage.getItem('storeToken');
    
    // For guest checkout, prompt for email if not already set
    if (!token && !guestEmail) {
      const email = prompt('Please enter your email address for the purchase:');
      if (!email) {
        toast.error('Email is required for checkout');
        return;
      }
      setGuestEmail(email);
      sessionStorage.setItem('guestCheckoutEmail', email);
    }
    
    try {
      const params = new URLSearchParams();
      if (trackId) params.append('track_id', trackId);
      if (releaseId) params.append('release_id', releaseId);
      params.append('format', format);
      
      // Store pending purchase info for after payment
      if (trackId) {
        sessionStorage.setItem('pendingPurchaseTrackIds', JSON.stringify([trackId]));
      }

      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = {};
      if (!token && (guestEmail || sessionStorage.getItem('guestCheckoutEmail'))) {
        body.email = guestEmail || sessionStorage.getItem('guestCheckoutEmail');
      }

      const res = await fetch(`${API_URL}/api/store/checkout?${params}`, {
        method: 'POST',
        headers,
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }

      // Redirect to PayPal
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message || 'An error occurred during checkout');
    }
  };

  const handleDownload = async (trackId, format = 'mp3') => {
    const token = localStorage.getItem('storeToken');
    const email = guestEmail || sessionStorage.getItem('guestCheckoutEmail');
    
    try {
      let url = `${API_URL}/api/store/tracks/${trackId}/download?format=${format}`;
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (email) {
        url += `&email=${encodeURIComponent(email)}`;
      } else {
        // Prompt for email if not available
        const inputEmail = prompt('Please enter the email address you used for purchase:');
        if (!inputEmail) {
          toast.error('Email is required to download');
          return;
        }
        url += `&email=${encodeURIComponent(inputEmail)}`;
        setGuestEmail(inputEmail);
        sessionStorage.setItem('guestCheckoutEmail', inputEmail);
      }

      const res = await fetch(url, { headers });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Download failed');
      }

      // Open the presigned S3 URL in a new tab to trigger download
      window.open(data.download_url, '_blank');
      
      toast.success(`${format.toUpperCase()} download started!`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRecoverPayment = async (e) => {
    e.preventDefault();
    if (!recoveryEmail || !recoveryPaypalId) {
      toast.error('Please enter both email and PayPal Payment ID');
      return;
    }
    
    setRecovering(true);
    try {
      const res = await fetch(`${API_URL}/api/store/checkout/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recoveryEmail
        })
      });
      
      const data = await res.json();
      
      if (data.status === 'recovered' || data.status === 'already_complete') {
        toast.success(data.message);
        setShowRecoveryModal(false);
        // Update guest email for downloads
        setGuestEmail(recoveryEmail);
        sessionStorage.setItem('guestCheckoutEmail', recoveryEmail);
        // Add to purchased track IDs
        if (data.track_id) {
          setPurchasedTrackIds(prev => [...new Set([...prev, data.track_id])]);
        }
      } else {
        toast.error(data.message || data.detail || 'Could not recover payment');
      }
    } catch (err) {
      toast.error('Failed to recover payment. Please contact support.');
    } finally {
      setRecovering(false);
    }
  };

  const isPurchased = (trackId, releaseId) => {
    return purchases.some(p => 
      p.track_id === trackId || p.release_id === releaseId
    );
  };

  const getTrackPrice = (track) => {
    // Always use base price - discount_price was removed from UI
    return track.price || 1.19;
  };

  if (loading) {
    return (
      <div className="store-page">
        <div className="store-loading">Loading store...</div>
      </div>
    );
  }

  return (
    <div className="store-page" data-testid="store-page">
      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="payment-overlay">
          <div className="payment-modal">
            <div className="spinner-large" />
            <h3>Processing Payment...</h3>
            <p>Please wait while we confirm your purchase.</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="store-hero">
        <div className="store-hero-bg"></div>
        <div className="store-hero-overlay"></div>
        <div className="container store-hero-content">
          <h1>Music Store</h1>
          <p className="store-hero-tagline">Preview, Purchase & Download</p>
          
          {user && (
            <div className="store-user-area">
              <div className="user-info">
                <User size={18} />
                <span>{user.name || user.email}</span>
                <button onClick={handleLogout} className="logout-btn">
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          )}
          
          {/* Payment Recovery Link */}
          <button 
            className="recover-payment-btn"
            onClick={() => setShowRecoveryModal(true)}
            title="Recover a payment that was completed but not recorded"
          >
            Having trouble downloading? Recover your payment
          </button>
        </div>
      </section>

      {/* Releases Grid */}
      <section className="store-content">
        <div className="container">
          {releases.length === 0 ? (
            <div className="no-releases">
              <Music size={48} />
              <h3>No releases available yet</h3>
              <p>Check back soon for new music!</p>
            </div>
          ) : (
            <div className="releases-list">
              {releases.map(release => {
                const releaseTracks = tracks[release.id] || [];
                const albumPrice = releaseTracks.reduce((sum, t) => sum + getTrackPrice(t), 0) * 0.9;
                const albumPurchased = isPurchased(null, release.id);

                return (
                  <div key={release.id} className="release-store-card" data-testid={`release-${release.id}`}>
                    <div className="release-header">
                      <img 
                        src={getImageUrl(release.artwork)} 
                        alt={release.title}
                        className="release-artwork"
                      />
                      <div className="release-info">
                        <h2>{release.title}</h2>
                        <p className="release-artist">{release.artist_name}</p>
                        <p className="release-genre">{release.genre}</p>
                        
                        {/* Album purchase option - hidden by default, set showAlbumOption to true to enable */}
                        {false && releaseTracks.length > 0 && !albumPurchased && (
                          <button 
                            className="buy-album-btn"
                            onClick={() => handleBuy(null, release.id, `${release.artist_name} - ${release.title}`, albumPrice)}
                          >
                            <ShoppingCart size={16} />
                            Buy Album - ${albumPrice.toFixed(2)}
                            <span className="discount-note">10% off</span>
                          </button>
                        )}
                        
                        {albumPurchased && (
                          <div className="purchased-badge">
                            <Download size={16} />
                            Purchased
                          </div>
                        )}
                      </div>
                    </div>

                    {releaseTracks.length > 0 ? (
                      <div className="tracks-list">
                        {releaseTracks.map((track, idx) => {
                          const trackPurchased = isPurchased(track.id, release.id);
                          const price = getTrackPrice(track);

                          return (
                            <div key={track.id} className="track-row" data-testid={`track-${track.id}`}>
                              <span className="track-number">{idx + 1}</span>
                              
                              <div className="track-player-wrapper">
                                {track.audio_path ? (
                                  <AudioPlayer
                                    trackId={track.id}
                                    trackTitle={track.title}
                                    artistName={track.artist_name}
                                    previewStart={track.preview_start || 30}
                                    previewDuration={30}
                                  />
                                ) : (
                                  <div className="track-info-only">
                                    <span className="track-title">{track.title}</span>
                                    <span className="no-preview">Preview coming soon</span>
                                  </div>
                                )}
                              </div>

                              <div className="track-actions">
                                {trackPurchased || purchasedTrackIds.includes(track.id) ? (
                                  <div className="download-buttons">
                                    <button 
                                      className="download-btn"
                                      onClick={() => handleDownload(track.id, 'mp3')}
                                    >
                                      MP3
                                    </button>
                                    <button 
                                      className="download-btn"
                                      onClick={() => handleDownload(track.id, 'wav')}
                                    >
                                      WAV
                                    </button>
                                  </div>
                                ) : (
                                  <div className="buy-options">
                                    <button 
                                      className="buy-track-btn mp3"
                                      onClick={() => handleBuy(track.id, null, `${track.artist_name} - ${track.title}`, MP3_PRICE, 'mp3')}
                                      title="Buy MP3 format"
                                    >
                                      <CreditCard size={14} />
                                      MP3 CA${MP3_PRICE.toFixed(2)}
                                    </button>
                                    <button 
                                      className="buy-track-btn wav"
                                      onClick={() => handleBuy(track.id, null, `${track.artist_name} - ${track.title}`, WAV_PRICE, 'wav')}
                                      title="Buy WAV format (higher quality)"
                                    >
                                      WAV CA${WAV_PRICE.toFixed(2)}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="no-tracks">
                        <p>No tracks available for purchase yet</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={e => e.stopPropagation()}>
            <h2>{authMode === 'login' ? 'Sign In' : 'Create Account'}</h2>
            
            <form onSubmit={handleAuth}>
              {authMode === 'register' && (
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" name="name" placeholder="Your name" />
                </div>
              )}
              
              <div className="form-group">
                <label>Email</label>
                <input type="email" name="email" required placeholder="your@email.com" />
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <input type="password" name="password" required placeholder="••••••••" minLength={6} />
              </div>

              <button type="submit" className="auth-submit-btn">
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="auth-switch">
              {authMode === 'login' ? (
                <p>
                  Don't have an account?{' '}
                  <button onClick={() => setAuthMode('register')}>Sign up</button>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button onClick={() => setAuthMode('login')}>Sign in</button>
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Recovery Modal */}
      {showRecoveryModal && (
        <div className="auth-modal-overlay" onClick={() => setShowRecoveryModal(false)}>
          <div className="auth-modal recovery-modal" onClick={e => e.stopPropagation()}>
            <h2>Recover Your Payment</h2>
            <p className="recovery-subtitle">
              If you completed a PayPal payment but didn't receive your download, 
              enter the email you used during checkout.
            </p>
            
            <form onSubmit={handleRecoverPayment}>
              <div className="form-group">
                <label>Email used for purchase</label>
                <input 
                  type="email" 
                  value={recoveryEmail}
                  onChange={e => setRecoveryEmail(e.target.value)}
                  required 
                  placeholder="your@email.com" 
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={recovering}>
                {recovering ? 'Recovering...' : 'Find My Purchase'}
              </button>
            </form>

            <button 
              className="modal-close-btn"
              onClick={() => setShowRecoveryModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Download Modal - Shows after successful payment */}
      {showDownloadModal && purchasedTrack && (
        <div className="auth-modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="auth-modal download-modal" onClick={e => e.stopPropagation()}>
            <div className="download-success-icon">✓</div>
            <h2>Payment Successful!</h2>
            <p className="download-track-info">
              <strong>{purchasedTrack.title}</strong><br />
              by {purchasedTrack.artist_name}
            </p>
            
            <p className="download-instruction">Click below to download your {purchasedFormat.toUpperCase()} track:</p>
            
            <div className="download-buttons">
              {purchasedFormat === 'mp3' && purchasedTrack.has_mp3 && (
                <button 
                  className="download-btn mp3"
                  onClick={() => handleDownload(purchasedTrack.id, 'mp3')}
                >
                  <Download size={18} />
                  Download MP3
                </button>
              )}
              {purchasedFormat === 'wav' && purchasedTrack.has_wav && (
                <button 
                  className="download-btn wav"
                  onClick={() => handleDownload(purchasedTrack.id, 'wav')}
                >
                  <Download size={18} />
                  Download WAV
                </button>
              )}
            </div>
            
            <p className="download-note">
              A download link has also been sent to your email.
            </p>

            <button 
              className="modal-close-btn"
              onClick={() => setShowDownloadModal(false)}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePage;
